// esercizi-api.js — amm-v3 (A4, ondata 31): parte PURA della vista "Bilanci e
// archivio" (pagina 'esercizi' — slot documenti d'esercizio per anno),
// testabile sotto node (contract test server/src/lib/amm-v3-esercizi.test.js).
//
// PRINCIPI (rito A1/A2/A3/proc-v3):
//  - TESTI VERBATIM dal v1 (admin-module.js, renderEsercizi r.2841-2933):
//    pinnati dal contract test.
//  - La STRUTTURA (slot + extra) arriva GIÀ pronta dal server (archivioEsercizio
//    in amm-esercizi.js): il v3 la mostra, non la ricalcola. Le funzioni pure
//    qui sono solo testi/chip (chip allegati, troncamento nota, anno risolto).
//  - STATO CONDIVISO col v1: anno su AMM.eserciziAnno (r.756, impostato al primo
//    render dall'anno più recente) — flip di flag senza perdite.
//  - DELEGHE: editEsercizioDoc (modale documento con allegati), esdStatoBadge
//    (HTML badge), delSafe (elimina), isAdmin (bottoni admin), toast. ZERO
//    bypass di render: il modale v1 chiama l'onSaved che GLI PASSA il v3 (mai
//    renderEsercizi diretto dal v3) ⇒ NIENTE interceptor.
//  - MAI 'nav:badge' o canali nuovi da client/src/amm-v3/**.

import { apiPathFn } from './api.js';

export const AE_DELEGATED_METHODS = [
  'renderPage',        // il seam stesso (wrap) + refresh post-azione
  'renderEsercizi',    // la vista v1 wrappata (drift guard: rename ⇒ rosso)
  'toast',             // esito eliminazione (stesso canale del v1)
  'isAdmin',           // bottone elimina solo admin (parità r.2880/2981)
  'delSafe',           // eliminazione con alert d'errore (primitiva v1 r.878)
  'editEsercizioDoc',  // modale documento (+ Carica / modifica) — resta v1
  'esdStatoBadge',     // badge Presente/Bozza/Mancante (HTML v1, iniettato)
];

export const AE_API_PATHS = [
  'esercizi.anni',    // GET /esercizi → { anni }
  'esercizi.get',     // GET /esercizi/:anno → { slots, extra }
  'esercizi.remove',  // DELETE /esercizi-documenti/:id
];

// ---- TESTI VERBATIM dal v1 (pinnati dal contract test) ----
export const AE_TEXTS = {
  title: 'Bilanci e archivio',
  sub: 'documenti definitivi per esercizio · bilanci, dichiarazioni, verbali',
  altriTitolo: 'Altri documenti',
  addCustom: '+ Documento con tipo personalizzato',
  carica: '+ Carica',
  slotVuoto: 'Nessun documento caricato.',
  nessunAllegato: 'nessun allegato',
  extraVuoto: 'Nessun documento extra per questo esercizio.',
  confirmElimina: 'Eliminare questo documento?',
  toastEliminato: 'Documento eliminato',
  erroreAnni: 'Errore nel caricamento degli anni: ',
  erroreArchivioPre: "Errore nel caricamento dell'archivio ",
};

// chip allegati (r.2868-2870): "N allegato/i" oppure "nessun allegato"
export function allegatiChip(d) {
  const n = (d.attachments || []).length;
  if (!n) return { testo: AE_TEXTS.nessunAllegato, ha: false };
  return { testo: n + ' allegat' + (n === 1 ? 'o' : 'i'), ha: true };
}

// nota troncata a 60 char con ellissi (r.2871)
export function notaTronca(note) {
  if (!note) return '';
  const s = String(note);
  return s.length > 60 ? s.slice(0, 60) + '…' : s;
}

export function erroreArchivio(anno, msg) { return AE_TEXTS.erroreArchivioPre + anno + ': ' + msg; }

// ---- STATO condiviso col v1 ----
// Risolve l'anno effettivo (r.2847-2850): anni dal server (o [anno corrente] se
// vuoto); se l'anno corrente su AMM è null o non è tra gli anni, si prende il
// più recente (anni[0]). Write-through su AMM.eserciziAnno (parità di stato).
export function risolviAnno(A, anniResp) {
  let anni = (anniResp && anniResp.anni) || [];
  if (!anni.length) anni = [new Date().getFullYear()];
  if (A.eserciziAnno == null || anni.indexOf(A.eserciziAnno) === -1) A.eserciziAnno = anni[0];
  return { anni, anno: A.eserciziAnno };
}

// tutti i documenti dell'archivio (slots + extra) — per findDoc (r.2917-2919)
export function tuttiDocumenti(slots, extra) {
  const fromSlots = (slots || []).reduce((acc, s) => acc.concat(s.documenti || []), []);
  return fromSlots.concat(extra || []);
}

// ---- LETTURA dati (composizione v1 r.2845-2865): anni poi archivio dell'anno
// risolto. La risoluzione dell'anno fa write-through su AMM (parità). ----
export async function readEsercizi(win) {
  const A = win.AMM;
  let anniResp;
  try { anniResp = await apiPathFn(win, 'esercizi.anni').call(null); }
  catch (e) { const err = new Error(AE_TEXTS.erroreAnni + (e && e.message ? e.message : e)); err.fase = 'anni'; throw err; }
  const { anni, anno } = risolviAnno(A, anniResp);
  let arch;
  try { arch = await apiPathFn(win, 'esercizi.get').call(null, anno); }
  catch (e) { const err = new Error(erroreArchivio(anno, e && e.message ? e.message : e)); err.fase = 'archivio'; err.anni = anni; err.anno = anno; throw err; }
  return { anni, anno, slots: arch.slots || [], extra: arch.extra || [] };
}

// ---- AZIONI e DELEGHE (stessi punti del v1) ----
// Editor documento (modale v1). Il v3 passa il PROPRIO onSaved (refresh v3):
// così il modale non chiama mai renderEsercizi diretto (niente bypass/interceptor).
export function apriEditorDoc(win, anno, rec, onSaved) { win.AMM.editEsercizioDoc(anno, rec, onSaved); }
// Elimina (delSafe v1: alert d'errore se fallisce), poi onOk (toast + refresh v3).
export function eliminaDoc(win, id, onOk) { return win.AMM.delSafe(apiPathFn(win, 'esercizi.remove').call(null, id), onOk); }
